import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { geocodeClient } from '../../services/clients'
import { onQuerySuccess } from '../../utils/queryResponse'

const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

// Set in .env.* as REACT_APP_GOOGLE_MAPS_API_KEY (a Maps Embed API key with
// billing enabled) — baked into the bundle at build time like the other
// REACT_APP_* values in this app.
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

// Street-level zoom — comparable to the ~650m-wide box the previous OSM embed
// used.
const DEFAULT_ZOOM = 16

// Exported so the inline card preview and this full-size modal render the exact
// same map from one implementation — see inlineAddressMap.jsx.
export const buildEmbedUrl = (latitude, longitude, zoom = DEFAULT_ZOOM) => {
    const params = new URLSearchParams({
        key: GOOGLE_MAPS_API_KEY || '',
        q: `${latitude},${longitude}`,
        zoom: String(zoom),
    })
    return `https://www.google.com/maps/embed/v1/place?${params.toString()}`
}

export const hasCoords = (lat, lng) => typeof lat === 'number' && typeof lng === 'number'

/**
 * Resolves a client's coordinates, lazily and at most once per client.
 *
 * If the client record already carries latitude/longitude they are used as-is
 * and no request is made. Otherwise the backend is asked to geocode the client
 * on demand (GET /clients/:id/geocode, company-scoped, caches server-side) — so
 * old clients never need a bulk backfill, they resolve the first time someone
 * actually looks at them.
 *
 * `requestedFor` deliberately lives in a ref rather than state: it survives
 * re-renders and modal open/close cycles, which is what stops a card that
 * re-renders (or a modal reopened five times) from re-asking the backend for
 * coordinates it already resolved — or failed to resolve.
 *
 * `active` gates the request so the modal only geocodes once it is actually
 * opened, while the always-visible inline preview simply passes true.
 */
export const useClientCoordinates = (clientId, latitude, longitude, active = true) => {
    const [resolved, setResolved] = useState({ latitude, longitude, approximate: false })
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const requestedFor = useRef(null)

    useEffect(() => {
        if (!active) return
        // Already asked about this exact client — whatever is in state (coords,
        // or the failure flag) is still the current answer, so leave it alone.
        if (requestedFor.current === clientId && !hasCoords(latitude, longitude)) return

        setResolved({ latitude, longitude, approximate: false })
        setFailed(false)

        if (hasCoords(latitude, longitude) || !clientId) return

        requestedFor.current = clientId
        setLoading(true)
        geocodeClient(clientId)
            .then((data) => onQuerySuccess(data, (coords) => {
                if (hasCoords(coords?.latitude, coords?.longitude)) {
                    setResolved(coords)
                } else {
                    setFailed(true)
                }
            }))
            .catch(() => setFailed(true))
            .finally(() => setLoading(false))
    }, [active, clientId, latitude, longitude])

    return { coords: resolved, loading, failed, ready: hasCoords(resolved.latitude, resolved.longitude) }
}

// The enlarged location view — the address itself is always shown as plain
// text alongside (and above) it, so this only ever supplements the address,
// never replaces it. Uses the Google Maps Embed API (requires
// REACT_APP_GOOGLE_MAPS_API_KEY, billing enabled). Geocoding is lazy: see
// useClientCoordinates above.
const AddressMapModal = ({ display, toggle, address, clientId, latitude, longitude }) => {
    const { t } = useTranslation()
    const { coords: resolved, loading, failed, ready } = useClientCoordinates(clientId, latitude, longitude, display)

    return (
        // "Enlarge" has to actually enlarge: this opens edge-to-edge rather than
        // as a default ~500px dialog, and the map below stretches to whatever
        // height is left instead of a fixed 360px strip. No `centered` — the
        // dialog already fills the viewport, and the two rules fight.
        <Modal show={display} onHide={() => toggle(false)} backdropClassName="level-2"
            contentClassName="sbr-modal sbr-mapmodal" dialogClassName="sbr-mapmodal-dialog" fullscreen>
            <Modal.Body>
                <div className="sbr sbr-mapmodal-in">
                    <div className="sbm-head">
                        <div className="htx">
                            <div className="ttl">{t('addressMap.location')}</div>
                            <div className="sub">{address}</div>
                        </div>
                        <button type="button" className="x" onClick={() => toggle(false)} aria-label={t('common.close')}><XIcon /></button>
                    </div>
                    <div className="sbr-mapmodal-body">
                        {loading &&
                            <p className="text-muted mb-0"><small>{t('addressMap.locating')}</small></p>}
                        {!loading && !ready && failed &&
                            <p className="text-muted mb-0"><small>{t('addressMap.notFound')}</small></p>}
                        {!loading && !ready && !failed &&
                            <p className="text-muted mb-0"><small>{t('addressMap.noCoordinates')}</small></p>}
                        {!loading && ready && resolved.approximate &&
                            <p className="text-muted mb-1"><small>{t('addressMap.nearestMatch')}</small></p>}
                        {!loading && ready &&
                            <iframe
                                title={t('addressMap.clientLocation')}
                                className="sbr-mapmodal-frame"
                                src={buildEmbedUrl(resolved.latitude, resolved.longitude)}
                            />}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default AddressMapModal
